export const WALKTHROUGH_IDS = ["appHome"] as const;

export type WalkthroughId = (typeof WALKTHROUGH_IDS)[number];

const STORAGE_PREFIX = "konstrulog.walkthroughs.v1:";

export function walkthroughStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

type WalkthroughFlags = Partial<Record<WalkthroughId, boolean>> & Record<string, boolean>;

function isWalkthroughId(value: string): value is WalkthroughId {
  return (WALKTHROUGH_IDS as readonly string[]).includes(value);
}

function getStorage(): Pick<Storage, "getItem" | "setItem" | "removeItem"> | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

function readFlags(userId: string): WalkthroughFlags {
  const storage = getStorage();
  if (!storage || userId.length === 0) return {};
  try {
    const raw = storage.getItem(walkthroughStorageKey(userId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const flags: WalkthroughFlags = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") flags[key] = value;
    }
    return flags;
  } catch {
    return {};
  }
}

function writeFlags(userId: string, flags: WalkthroughFlags): void {
  const storage = getStorage();
  if (!storage || userId.length === 0) return;
  try {
    storage.setItem(walkthroughStorageKey(userId), `${JSON.stringify(flags, null, 2)}\n`);
  } catch {
    /* ignore quota / private mode */
  }
}

/** True only when the named flag is the boolean `true`. Missing or `false` means show the tour. */
export function isWalkthroughDone(userId: string, id: WalkthroughId): boolean {
  return readFlags(userId)[id] === true;
}

/** Writes `true` or `false` without dropping other flags. */
export function setWalkthroughDone(userId: string, id: WalkthroughId, done: boolean): void {
  if (!isWalkthroughId(id)) return;
  const flags = readFlags(userId);
  flags[id] = done;
  writeFlags(userId, flags);
}

/** Removes every walkthrough flag for this user. */
export function clearWalkthroughs(userId: string): void {
  const storage = getStorage();
  if (!storage || userId.length === 0) return;
  try {
    storage.removeItem(walkthroughStorageKey(userId));
  } catch {
    /* ignore private mode */
  }
}
